import type { Playlist, CreatePlaylist, SavePlaylist } from '@/models/media-network/Playlist';
import type { PlaylistSchedule, SavePlaylistSchedule } from '@/models/media-network/PlaylistSchedule';
import type {
    PlaylistConfiguration, SavePlaylistConfiguration, EffectivePlaylistConfiguration,
} from '@/models/media-network/PlaylistConfiguration';
import type {
    Advertiser, CreateAdvertiser, SaveAdvertiser, Tag, CreateTag,
    AdvertiserGrant, GrantAdvertiser, SaveBrandAdvertiser,
} from '@/models/media-network/Advertiser';
import type {
    Creative, CreateCreative, SetAttribution, CreateRenditionIntent, RenditionUploadIntent,
    CommitRendition, RenditionReadUri, DisplayProfile, AppDisplayProfiles, AddPlaylistItemResult,
} from '@/models/media-network/Creative';
import { mediaNetworkRequest } from './mediaNetworkClient';

export const mediaNetworkApi = {
    // ----- Media library (network-level, shared by every feature) -----

    listCreatives: (brandId: string) =>
        mediaNetworkRequest<Creative[]>(`media-network/creatives?brandId=${brandId}`),
    getCreative: (id: string) =>
        mediaNetworkRequest<Creative>(`media-network/creatives/${id}`),
    createCreative: (item: CreateCreative) =>
        mediaNetworkRequest<{ id: string }>(`media-network/creatives`, { method: 'POST', body: JSON.stringify(item) }),
    renameCreative: (id: string, name: string) =>
        mediaNetworkRequest<void>(`media-network/creatives/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
    // Refused by the API while a playlist still uses it; the error names them.
    deleteCreative: (id: string) =>
        mediaNetworkRequest<void>(`media-network/creatives/${id}`, { method: 'DELETE' }),
    // Advertiser, asset code and tags in one call, because it is one dialog and one Save.
    setAttribution: (id: string, item: SetAttribution) =>
        mediaNetworkRequest<void>(`media-network/creatives/${id}/attribution`, {
            method: 'PUT', body: JSON.stringify(item),
        }),

    // Renditions. Every call pairs the rendition with its creative — a rendition id carries no brand, so the
    // creative is what the API can tenancy-guard on.
    createRenditionIntent: (creativeId: string, item: CreateRenditionIntent) =>
        mediaNetworkRequest<RenditionUploadIntent>(`media-network/creatives/${creativeId}/renditions/upload-intent`, {
            method: 'POST', body: JSON.stringify(item),
        }),
    // Same handshake, and the SAME commit call finishes it. The difference is on the backend: what commits
    // replaces this rendition's file, and the original stays serveable until the new bytes are confirmed.
    createRenditionReplaceIntent: (creativeId: string, renditionId: string, item: CreateRenditionIntent) =>
        mediaNetworkRequest<RenditionUploadIntent>(
            `media-network/creatives/${creativeId}/renditions/${renditionId}/replace-intent`,
            { method: 'POST', body: JSON.stringify(item) },
        ),
    commitRendition: (creativeId: string, renditionId: string, item: CommitRendition) =>
        mediaNetworkRequest<void>(`media-network/creatives/${creativeId}/renditions/${renditionId}/commit`, {
            method: 'POST', body: JSON.stringify(item),
        }),
    deleteRendition: (creativeId: string, renditionId: string) =>
        mediaNetworkRequest<void>(`media-network/creatives/${creativeId}/renditions/${renditionId}`, {
            method: 'DELETE',
        }),
    // Fetched when a preview opens, never cached: the URL expires, so a stale one shows a broken image.
    getRenditionReadUri: (creativeId: string, renditionId: string) =>
        mediaNetworkRequest<RenditionReadUri>(
            `media-network/creatives/${creativeId}/renditions/${renditionId}/read-uri`),

    // ----- Display profiles -----

    listDisplayProfiles: () =>
        mediaNetworkRequest<DisplayProfile[]>(`media-network/display-profiles`),
    listBrandDisplayProfiles: (brandId: string) =>
        mediaNetworkRequest<AppDisplayProfiles[]>(`media-network/display-profiles/brand/${brandId}`),
    setBrandDisplayProfiles: (brandId: string, appType: number, displayProfileIds: string[]) =>
        mediaNetworkRequest<void>(`media-network/display-profiles/brand/${brandId}`, {
            method: 'PUT', body: JSON.stringify({ appType, displayProfileIds }),
        }),

    // ----- Ad Banner: playlists -----

    list: (brandId: string) =>
        mediaNetworkRequest<Playlist[]>(`media-network/playlists?brandId=${brandId}`),
    get: (id: string) =>
        mediaNetworkRequest<Playlist>(`media-network/playlists/${id}`),
    create: (item: CreatePlaylist) =>
        mediaNetworkRequest<{ id: string }>(`media-network/playlists`, { method: 'POST', body: JSON.stringify(item) }),
    update: (id: string, item: SavePlaylist) =>
        mediaNetworkRequest<void>(`media-network/playlists/${id}`, { method: 'PUT', body: JSON.stringify(item) }),
    remove: (id: string) =>
        mediaNetworkRequest<void>(`media-network/playlists/${id}`, { method: 'DELETE' }),

    // Items are references to library creatives. Adding returns the shapes the creative cannot fill — a
    // WARNING, not an error: it was added and is simply skipped on those surfaces.
    addItem: (playlistId: string, creativeId: string) =>
        mediaNetworkRequest<AddPlaylistItemResult>(`media-network/playlists/${playlistId}/items`, {
            method: 'POST', body: JSON.stringify({ creativeId }),
        }),
    // Removes the reference only. The creative stays in the library.
    removeItem: (playlistId: string, itemId: string) =>
        mediaNetworkRequest<void>(`media-network/playlists/${playlistId}/items/${itemId}`, { method: 'DELETE' }),
    // Must list EVERY item exactly once — the backend rejects a partial list rather than silently appending
    // whatever was left out.
    reorderItems: (playlistId: string, orderedItemIds: string[]) =>
        mediaNetworkRequest<void>(`media-network/playlists/${playlistId}/items/order`, {
            method: 'PUT', body: JSON.stringify({ orderedItemIds }),
        }),

    // Schedules
    listSchedules: (playlistId: string) =>
        mediaNetworkRequest<PlaylistSchedule[]>(`media-network/playlists/${playlistId}/schedules`),
    createSchedule: (playlistId: string, item: SavePlaylistSchedule) =>
        mediaNetworkRequest<{ id: string }>(`media-network/playlists/${playlistId}/schedules`, {
            method: 'POST', body: JSON.stringify(item),
        }),
    updateSchedule: (playlistId: string, scheduleId: string, item: SavePlaylistSchedule) =>
        mediaNetworkRequest<void>(`media-network/playlists/${playlistId}/schedules/${scheduleId}`, {
            method: 'PUT', body: JSON.stringify(item),
        }),
    deleteSchedule: (playlistId: string, scheduleId: string) =>
        mediaNetworkRequest<void>(`media-network/playlists/${playlistId}/schedules/${scheduleId}`, {
            method: 'DELETE',
        }),

    // Configuration — banner playback, so it stays under playlists rather than at the network level.
    listConfigurations: (brandId: string) =>
        mediaNetworkRequest<PlaylistConfiguration[]>(`media-network/playlists/configuration?brandId=${brandId}`),
    saveConfiguration: (item: SavePlaylistConfiguration) =>
        mediaNetworkRequest<{ id: string }>(`media-network/playlists/configuration`, {
            method: 'PUT', body: JSON.stringify(item),
        }),
    deleteConfiguration: (id: string, brandId: string) =>
        mediaNetworkRequest<void>(`media-network/playlists/configuration/${id}?brandId=${brandId}`, {
            method: 'DELETE',
        }),
    getEffectiveConfiguration: (locationId: string) =>
        mediaNetworkRequest<EffectivePlaylistConfiguration>(
            `media-network/playlists/configuration/effective/${locationId}`),

    // ----- Advertisers and tags (network-level attribution reference data) -----

    listAdvertisers: (brandId: string) =>
        mediaNetworkRequest<Advertiser[]>(`media-network/advertisers?brandId=${brandId}`),
    createAdvertiser: (item: CreateAdvertiser) =>
        mediaNetworkRequest<{ id: string }>(`media-network/advertisers`, { method: 'POST', body: JSON.stringify(item) }),
    updateAdvertiser: (id: string, item: SaveAdvertiser) =>
        mediaNetworkRequest<void>(`media-network/advertisers/${id}`, { method: 'PUT', body: JSON.stringify(item) }),
    // Refused by the API while creatives still attribute to them; the error says disabling is the action.
    deleteAdvertiser: (id: string) =>
        mediaNetworkRequest<void>(`media-network/advertisers/${id}`, { method: 'DELETE' }),

    // The brand's own half of a grant — its veto, and its account number for a TouchTab advertiser. The only
    // thing a brand may change about an advertiser it does not own.
    setBrandAdvertiser: (advertiserId: string, brandId: string, item: SaveBrandAdvertiser) =>
        mediaNetworkRequest<void>(`media-network/advertisers/${advertiserId}/brand?brandId=${brandId}`, {
            method: 'PUT', body: JSON.stringify(item),
        }),

    // ----- The TouchTab roster. Super-admin only; the API returns 403 rather than an empty list. -----

    listPlatformAdvertisers: () =>
        mediaNetworkRequest<Advertiser[]>(`media-network/admin/advertisers`),
    createPlatformAdvertiser: (item: SaveAdvertiser) =>
        mediaNetworkRequest<{ id: string }>(`media-network/admin/advertisers`, {
            method: 'POST', body: JSON.stringify(item),
        }),
    updatePlatformAdvertiser: (id: string, item: SaveAdvertiser) =>
        mediaNetworkRequest<void>(`media-network/admin/advertisers/${id}`, {
            method: 'PUT', body: JSON.stringify(item),
        }),
    deletePlatformAdvertiser: (id: string) =>
        mediaNetworkRequest<void>(`media-network/admin/advertisers/${id}`, { method: 'DELETE' }),

    // Advertiser -> brands: one agreement, then the clients it runs at.
    listAdvertiserGrants: (advertiserId: string) =>
        mediaNetworkRequest<AdvertiserGrant[]>(`media-network/admin/advertisers/${advertiserId}/grants`),
    grantAdvertiser: (advertiserId: string, item: GrantAdvertiser) =>
        mediaNetworkRequest<void>(`media-network/admin/advertisers/${advertiserId}/grants`, {
            method: 'POST', body: JSON.stringify(item),
        }),
    revokeAdvertiser: (advertiserId: string, brandId: string) =>
        mediaNetworkRequest<void>(`media-network/admin/advertisers/${advertiserId}/grants/${brandId}`, {
            method: 'DELETE',
        }),

    listTags: (brandId: string) =>
        mediaNetworkRequest<Tag[]>(`media-network/tags?brandId=${brandId}`),
    // Idempotent by name: an existing tag comes back rather than an error, because tagging is pick-or-type.
    createTag: (item: CreateTag) =>
        mediaNetworkRequest<{ id: string }>(`media-network/tags`, { method: 'POST', body: JSON.stringify(item) }),
    renameTag: (id: string, brandId: string, name: string) =>
        mediaNetworkRequest<void>(`media-network/tags/${id}?brandId=${brandId}`, {
            method: 'PUT', body: JSON.stringify({ name }),
        }),
    deleteTag: (id: string, brandId: string) =>
        mediaNetworkRequest<void>(`media-network/tags/${id}?brandId=${brandId}`, { method: 'DELETE' }),
};

// Day parts moved to @/api/account-management/dayPartApi — they are owned by AccountManagement and are not
// playlist-specific, even though playlists is currently their only consumer.
